import draftActions from './draft-actions';
import syiActions from './syi-actions';
import logger from 'packages/logger';
import { getSyiSlice } from 'tradera-state/views/syi/selectors/syi';
import errorActions from 'tradera-state/views/syi/actions/error-actions';
import { openAddPhoto } from 'tradera-apps/syi/script/app_react/services/native-app-service';
import imageUploadService from 'tradera-apps/syi/script/app_react/services/image-upload-service';
import { throttledFetchImageAttributeSuggestions } from './attributes-actions';

const MAX_IMAGES_FOR_IMAGE_TO_COLORS = 2;

const setDraftImages = images => dispatch => {
    dispatch({
        type: 'SET_DRAFT_IMAGES',
        payload: images
    });

    dispatch(draftActions.updateDraftCanBeResumed());

    const removeNullAndUndefinedFilter = n => n;
    const draftImageIds = images
        .slice(0, MAX_IMAGES_FOR_IMAGE_TO_COLORS)
        .map(image => image.draftImage?.imageId)
        .filter(removeNullAndUndefinedFilter);
    if (draftImageIds.length > 0) {
        dispatch(throttledFetchImageAttributeSuggestions(draftImageIds));
    }
};

const setUploadedImageInNextFreeSlot = (imageId, imageUrl) => (
    dispatch,
    getState
) => {
    var slots = getSyiSlice(getState()).images.slots;
    var freeSlotIndex = -1;
    for (var i = 0; i < slots.length; i++) {
        if (!slots[i].imageId) {
            freeSlotIndex = i;
            break;
        }
    }
    if (freeSlotIndex > -1) {
        dispatch({
            type: 'SET_UPLOADED_IMAGE_IN_SLOT',
            slotIndex: freeSlotIndex,
            imageUrl: imageUrl,
            imageId: imageId
        });
        if (freeSlotIndex < MAX_IMAGES_FOR_IMAGE_TO_COLORS) {
            handleAttributeSuggestionsForSlot(dispatch, slots);
        }
    }
    dispatch(draftActions.updateDraftCanBeResumed());
};

const setUploadedImageInImageSlot = (slotIndex, imageId, imageUrl) => (
    dispatch,
    getState
) => {
    dispatch({
        type: 'SET_UPLOADED_IMAGE_IN_SLOT',
        slotIndex: slotIndex,
        imageUrl: imageUrl,
        imageId: imageId
    });
    if (slotIndex < MAX_IMAGES_FOR_IMAGE_TO_COLORS) {
        var slots = getSyiSlice(getState()).images.slots;
        handleAttributeSuggestionsForSlot(dispatch, slots);
    }
    dispatch(draftActions.updateDraftCanBeResumed());
};

const removeImageInSlot = slotIndex => (dispatch, getState) => {
    let slots = [...getSyiSlice(getState()).images.slots];
    // move images to the left
    for (var i = slotIndex; i < slots.length - 1; i++) {
        slots[i].imageUrl = slots[i + 1].imageUrl;
        slots[i].imageId = slots[i + 1].imageId;
        slots[i].rotationDegrees = slots[i + 1].rotationDegrees;
        slots[i].isListingImage = slots[i + 1].isListingImage;
        if (slots[i + 1].isListingImage) {
            slots[i].filename = slots[i + 1].filename;
            slots[i].imageGroupId = slots[i + 1].imageGroupId;
        }
    }

    // reset last slot
    slots[4].isUploading = false;
    slots[4].imageUrl = null;
    slots[4].imageId = null;
    slots[4].isListingImage = false;
    slots[4].filename = null;
    slots[4].imageGroupId = null;

    dispatch({
        type: 'SET_IMAGE_SLOTS',
        slots: slots
    });

    if (slotIndex < MAX_IMAGES_FOR_IMAGE_TO_COLORS) {
        handleAttributeSuggestionsForSlot(dispatch, slots);
    }
    dispatch(draftActions.updateDraftCanBeResumed());
};

const changeImagePosition = (from, to) => (dispatch, getState) => {
    var slots = getSyiSlice(getState()).images.slots;
    var slot = slots[from];
    slots.splice(from, 1);
    var len = slots.length;
    var newSlots = [];
    for (var i = 0; i < len; i++) {
        if (i == to) {
            newSlots.push(slot);
        }
        newSlots.push(slots[i]);
    }
    if (to == slots.length) {
        newSlots.push(slot);
    }
    dispatch({
        type: 'SET_IMAGE_SLOTS',
        slots: newSlots
    });
    const isImageToColorImageChanged =
        (from >= MAX_IMAGES_FOR_IMAGE_TO_COLORS &&
            to < MAX_IMAGES_FOR_IMAGE_TO_COLORS) ||
        (to >= MAX_IMAGES_FOR_IMAGE_TO_COLORS &&
            from < MAX_IMAGES_FOR_IMAGE_TO_COLORS);
    if (isImageToColorImageChanged) {
        handleAttributeSuggestionsForSlot(dispatch, newSlots);
    }
};

const removeImageWithId = imageId => (dispatch, getState) => {
    let slots = getSyiSlice(getState()).images.slots;
    let slotIndex = -1;
    for (var i = slots.length; i-- > 0; ) {
        if (slots[i].imageId === imageId) {
            slotIndex = i;
            break;
        }
    }
    if (slotIndex > -1) {
        dispatch(removeImageInSlot(slotIndex));
    }

    dispatch(draftActions.updateDraftCanBeResumed());
};

const rotateImage = slotIndex => (dispatch, getState) => {
    var slots = getSyiSlice(getState()).images.slots;
    var rotationDegrees = slots[slotIndex].rotationDegrees || 0;
    rotationDegrees += 90;
    if (rotationDegrees === 360) {
        rotationDegrees = 0;
    }
    dispatch({
        type: 'SET_IMAGE_ROTATION',
        slotIndex: slotIndex,
        rotationDegrees: rotationDegrees
    });
};

const setUploadFailed = errorMessage => dispatch => {
    dispatch({
        type: 'SET_IMAGE_UPLOAD_FAILED',
        payload: errorMessage
    });
};

const setUploading = numberOfSlots => dispatch => {
    dispatch({
        type: 'SET_IMAGES_UPLOADING',
        numberOfSlots: numberOfSlots
    });
};

const prepareUpload = (selectedFilesCount, freeSlotCount) => dispatch => {
    errorActions.setError('images', null);
    const numberOfFilesToUpload = Math.min(freeSlotCount, selectedFilesCount);
    dispatch(setUploading(numberOfFilesToUpload));
};

const setImageThunk = (freeSlotCount, fileInput, flow) => dispatch => {
    const selectedFilesCount = fileInput.files?.length || 1;
    dispatch(prepareUpload(selectedFilesCount, freeSlotCount));
    imageUploadService.uploadFromFileInput(fileInput, freeSlotCount, flow);
};

class MissingDraftId extends Error {}

const nativeAppOpenAddPhotoThunk = saveNowIfQueued => async (
    dispatch,
    getState
) => {
    dispatch(setIsUploadingWithApp(true));
    try {
        dispatch(syiActions.disableAutoSave());
        const currentDraftId = getSyiSlice(getState())?.draft?.draftId;
        const draft = await saveNowIfQueued(currentDraftId);
        const draftId = draft?.draftId || currentDraftId;
        if (!draftId) {
            throw new MissingDraftId();
        }
        await openAddPhoto(draftId);
    } catch (error) {
        if (error instanceof MissingDraftId) {
            logger('Missing draftId on app photo upload');
        } else if (!error.request || error.response) {
            // Do not log axios request errors, as they are client dependent (such as offline)
            logger(error);
        }
        dispatch(setUploadFailed('syi_images_photo_with_mobile_error'));
        dispatch(setIsUploadingWithApp(false));
        dispatch(syiActions.enableAutoSave());
    }
};

const fetchImagesFromDraft = (draftId, draftSocket) => async dispatch => {
    if (!draftId) {
        logger('DraftChanged event missing draft ID');
    }
    const imagesHaveChanged = await dispatch(
        draftActions.loadImagesFromDraft(draftId)
    );
    if (imagesHaveChanged) {
        dispatch(cancelMobileUpload(draftSocket));
    }
};

const setIsUploadingWithApp = isUploadingWithApp => dispatch => {
    dispatch({
        type: 'SET_MOBILE_UPLOAD',
        isUploadingWithApp
    });
};

const cancelMobileUpload = draftSocket => dispatch => {
    draftSocket.disconnect();
    dispatch(setIsUploadingWithApp(false));
    dispatch(syiActions.enableAutoSave());
};

const handleAttributeSuggestionsForSlot = (dispatch, slots) => {
    const imageIds = [];
    slots.slice(0, MAX_IMAGES_FOR_IMAGE_TO_COLORS).forEach(slot => {
        if (slot.imageId && !slot.isListingImage) {
            imageIds.push(slot.imageId);
        }
    });
    if (imageIds.length > 0) {
        dispatch(throttledFetchImageAttributeSuggestions(imageIds));
    } else {
        dispatch({
            type: 'RESET_IMAGE_ATTRIBUTES_SUGGESTIONS'
        });
    }
};

const reset = () => dispatch => {
    dispatch(setDraftImages([]));
    dispatch(draftActions.updateDraftCanBeResumed());
};

export default {
    setDraftImages,
    setUploadedImageInNextFreeSlot,
    setUploadedImageInImageSlot,
    removeImageInSlot,
    removeImageWithId,
    changeImagePosition,
    rotateImage,
    setUploadFailed,
    setUploading,
    prepareUpload,
    setImageThunk,
    nativeAppOpenAddPhotoThunk,
    fetchImagesFromDraft,
    cancelMobileUpload,
    setIsUploadingWithApp,
    reset
};
